import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Pagination } from '@shared/models/pagination.model';
import { SignedUrlObject } from '@shared/models/signedUrlObject.model';
import { BaseService } from '@shared/services/base.service';
import { Observable, map } from 'rxjs';

import {
  ReturnToSupplierHistoryDetail,
  ReturnToSupplierHistoryDetailResponse,
  ReturnToSupplierHistoryListContent,
  ReturnToSupplierHistorySearchCriteria
} from '../model/return-to-supplier-history.model';

@Injectable({ providedIn: 'root' })
export class ReturnToSupplierHistoryService extends BaseService {
  public override headers: HttpHeaders;

  constructor(private readonly http: HttpClient) {
    super();
    this.envService = this.env.services.returnToSupplierHistory;
    this.headers = new HttpHeaders(this.envService.headers);
  }

  getReturnToSupplierHistoryList(criteria: ReturnToSupplierHistorySearchCriteria) {
    const storeCode = this.retrieveStore().storeCode;
    const params = this.getParams(criteria);
    const url = this.getFullUrl(this.envService.list, { storeCode });
    return this.http.get<Pagination<ReturnToSupplierHistoryListContent>>(url, {
      headers: this.loaderHeaders(null),
      observe: 'body',
      params
    });
  }

  getReturnToSupplierHistoryView(returnNo: string) {
    const storeCode = this.retrieveStore().storeCode;
    const url = this.getFullUrl(this.envService.view, { storeCode, returnNo });
    return this.http
      .get<ReturnToSupplierHistoryDetailResponse>(url, {
        headers: this.loaderHeaders(null),
        observe: 'body'
      })
      .pipe(map((response) => this.mapDetailResponse(response)));
  }

  override getFileUrl(refId: string): Observable<SignedUrlObject> {
    const storeCode = this.retrieveStore().storeCode;
    const url = this.getFullUrl(this.envService.fileUrl, { storeCode });
    return this.http
      .post(
        url,
        { refId },
        {
          headers: this.loaderHeaders(null),
          observe: 'body',
          responseType: 'text'
        }
      )
      .pipe(map((signedUrl) => ({ refId, signedUrl }) as SignedUrlObject));
  }

  print(returnNo: string) {
    const storeCode = this.retrieveStore().storeCode;
    const url = this.getFullUrl(this.envService.print, { storeCode, returnNo });
    return this.http.post(
      url,
      {},
      {
        headers: this.loaderHeaders(null),
        observe: 'body',
        responseType: 'blob'
      }
    ) as unknown as Observable<Blob>;
  }

  private mapDetailResponse(response: ReturnToSupplierHistoryDetailResponse): ReturnToSupplierHistoryDetail {
    return {
      documentNo: response.documentNo,
      referenceNo: response.referenceNo,
      storeCode: response.storeCode,
      supplierCode: response.supplierCode,
      supplierName: response.supplierName,
      pdfPath: response.pdfPath,
      createdByName: response.createdByName,
      createdByLocalName: response.createdByLocalName,
      createdDate: response.createdDate,
      lastModifiedByName: response.lastModifiedByName,
      lastModifiedByLocalName: response.lastModifiedByLocalName,
      products: (response.returnToSupplierItems || []).map((item) => ({
        productName: item.productName,
        articleNo: item.articleNo,
        returnBarcode: item.barcode,
        returnUnit: item.unit,
        returnQuantity: item.qty,
        returnImages: (item.returnToSupplierItemImages || []).map((image) => ({
          filePath: image.path,
          no: image.no
        }))
      }))
    };
  }
}
